import React, { useEffect, useMemo, useState } from "react";
import {
  archiveTodoList,
  createTodoItem,
  createTodoList,
  deleteTodoItem,
  fetchTodoOverview,
  updateTodoItem,
  updateTodoList,
  updateTodoStatus
} from "../api";

const initialTaskForm = {
  title: "",
  description: "",
  priority: "medium",
  due_date: ""
};

const initialListForm = {
  name: "",
  description: "",
  color: "#1976d2"
};

const listColors = ["#1976d2", "#7c3aed", "#059669", "#ea580c", "#dc2626", "#0891b2"];

const columns = [
  { key: "todo", label: "To do" },
  { key: "in_progress", label: "In progress" },
  { key: "done", label: "Done" }
];

function isTodayDateString(value) {
  if (!value) {
    return false;
  }
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, "0");
  const dd = String(today.getDate()).padStart(2, "0");
  return value === `${yyyy}-${mm}-${dd}`;
}

function matchesFilter(item, filter) {
  if (filter === "overdue") {
    return Boolean(item.is_overdue);
  }
  if (filter === "today") {
    return Boolean(item.is_due_today || isTodayDateString(item.due_date));
  }
  if (filter === "high_priority") {
    return item.priority === "high";
  }
  return true;
}

function getPriorityBadgeClass(priority) {
  return `badge badge--${priority || "medium"}`;
}

function getTaskCardClass(item) {
  if (item.is_overdue) {
    return "task-card task-card--overdue";
  }
  if (item.is_due_today) {
    return "task-card task-card--today";
  }
  return "task-card";
}

function formatDueLabel(item) {
  if (!item.due_date) {
    return "No due date";
  }
  if (item.is_overdue) {
    return `${item.due_date} • Overdue`;
  }
  if (item.is_due_today || isTodayDateString(item.due_date)) {
    return `${item.due_date} • Due today`;
  }
  return item.due_date;
}

function KpiCard({ label, value, accentColor }) {
  return (
    <div className="kpi-card">
      <div className="kpi-card__label">{label}</div>
      <div className="kpi-card__value">{value}</div>
      <div className="kpi-card__accent" style={{ color: accentColor }} />
    </div>
  );
}

function TaskCard({ item, onStatusChange, onDelete, onEdit }) {
  return (
    <article className={getTaskCardClass(item)}>
      <div className="task-card__top">
        <div>
          <h4 className="task-title">{item.title}</h4>
        </div>
        <span className="badge badge--status">{item.status.replaceAll("_", " ")}</span>
      </div>

      <div className="task-description">{item.description || "No description yet."}</div>

      <div className="task-meta">
        <span>
          Priority <strong className={getPriorityBadgeClass(item.priority)}>{item.priority}</strong>
        </span>
        <span className={item.is_overdue ? "due-flag--overdue" : item.is_due_today ? "due-flag--today" : ""}>
          Due <strong>{formatDueLabel(item)}</strong>
        </span>
      </div>

      <div className="task-actions">
        {item.status !== "todo" ? (
          <button type="button" className="task-action" onClick={() => onStatusChange(item.id, "todo")}>
            Move to To do
          </button>
        ) : null}
        {item.status !== "in_progress" ? (
          <button type="button" className="task-action" onClick={() => onStatusChange(item.id, "in_progress")}>
            Move to In progress
          </button>
        ) : null}
        {item.status !== "done" ? (
          <button type="button" className="task-action" onClick={() => onStatusChange(item.id, "done")}>
            Move to Done
          </button>
        ) : null}
        <button type="button" className="task-action" onClick={() => onEdit(item)}>
          Edit
        </button>
        <button type="button" className="task-action task-action--danger" onClick={() => onDelete(item.id)}>
          Delete
        </button>
      </div>
    </article>
  );
}

export default function TodoPanel() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(initialTaskForm);
  const [filter, setFilter] = useState("all");
  const [editingItem, setEditingItem] = useState(null);
  const [editForm, setEditForm] = useState(initialTaskForm);
  const [listModalMode, setListModalMode] = useState("");
  const [listForm, setListForm] = useState(initialListForm);
  const [selectedListId, setSelectedListId] = useState(() => {
    const stored = window.localStorage.getItem("todo_active_list_id");
    return stored ? Number(stored) : null;
  });

  const applyResult = (result) => {
    const nextData = result?.data || null;
    setData(nextData);
    if (nextData?.active_list_id) {
      setSelectedListId(nextData.active_list_id);
      window.localStorage.setItem("todo_active_list_id", String(nextData.active_list_id));
    }
  };

  const loadData = async (nextListId = selectedListId) => {
    setLoading(true);
    setError("");
    try {
      const result = await fetchTodoOverview(nextListId);
      applyResult(result);
    } catch (err) {
      setError(err.message || "Failed to load todo module.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(selectedListId);
  }, [selectedListId]);

  const handleCreate = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = {
        ...form,
        list_id: selectedListId,
        due_date: form.due_date || null
      };
      const result = await createTodoItem(payload);
      applyResult(result);
      setForm(initialTaskForm);
    } catch (err) {
      setError(err.message || "Failed to create todo item.");
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (todoId, status) => {
    setError("");
    try {
      const result = await updateTodoStatus(todoId, { status, list_id: selectedListId });
      applyResult(result);
    } catch (err) {
      setError(err.message || "Failed to update todo status.");
    }
  };

  const handleEditOpen = (item) => {
    setError("");
    setEditingItem(item);
    setEditForm({
      title: item.title || "",
      description: item.description || "",
      priority: item.priority || "medium",
      due_date: item.due_date || ""
    });
  };

  const handleEditClose = () => {
    setEditingItem(null);
    setEditForm(initialTaskForm);
  };

  const handleEditSave = async (event) => {
    event.preventDefault();
    if (!editingItem) {
      return;
    }
    setSaving(true);
    setError("");
    try {
      const result = await updateTodoItem(editingItem.id, {
        ...editForm,
        list_id: selectedListId,
        due_date: editForm.due_date || null
      });
      applyResult(result);
      handleEditClose();
    } catch (err) {
      setError(err.message || "Failed to update todo item.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (todoId) => {
    setError("");
    try {
      const result = await deleteTodoItem(todoId, selectedListId);
      applyResult(result);
    } catch (err) {
      setError(err.message || "Failed to delete todo item.");
    }
  };

  const handleSelectList = async (listId) => {
    setSelectedListId(listId);
    window.localStorage.setItem("todo_active_list_id", String(listId));
    await loadData(listId);
  };

  const openCreateListModal = () => {
    setListModalMode("create");
    setListForm(initialListForm);
  };

  const openEditListModal = () => {
    if (!data?.active_list) {
      return;
    }
    setListModalMode("edit");
    setListForm({
      name: data.active_list.name || "",
      description: data.active_list.description || "",
      color: data.active_list.color || "#1976d2"
    });
  };

  const closeListModal = () => {
    setListModalMode("");
    setListForm(initialListForm);
  };

  const handleListSave = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const result = listModalMode === "edit" && data?.active_list_id
        ? await updateTodoList(data.active_list_id, listForm)
        : await createTodoList(listForm);
      applyResult(result);
      closeListModal();
    } catch (err) {
      setError(err.message || "Failed to save todo list.");
    } finally {
      setSaving(false);
    }
  };

  const handleArchiveList = async () => {
    if (!data?.active_list_id) {
      return;
    }
    setSaving(true);
    setError("");
    try {
      const result = await archiveTodoList(data.active_list_id);
      applyResult(result);
    } catch (err) {
      setError(err.message || "Failed to archive todo list.");
    } finally {
      setSaving(false);
    }
  };

  const items = data?.items || [];
  const totals = data?.totals || {};
  const lists = data?.lists || [];
  const activeList = data?.active_list || null;
  const filteredItems = useMemo(() => items.filter((item) => matchesFilter(item, filter)), [items, filter]);
  const groupedItems = useMemo(() => {
    return columns.reduce((accumulator, column) => {
      accumulator[column.key] = filteredItems.filter((item) => item.status === column.key);
      return accumulator;
    }, {});
  }, [filteredItems]);

  return (
    <section className="dashboard-grid">
      <div className="kpi-grid">
        <KpiCard label="Total tasks" value={totals.total_items ?? "-"} accentColor="#60a5fa" />
        <KpiCard label="To do" value={totals.todo_count ?? "-"} accentColor="#60a5fa" />
        <KpiCard label="In progress" value={totals.in_progress_count ?? "-"} accentColor="#c084fc" />
        <KpiCard label="Done" value={totals.done_count ?? "-"} accentColor="#34d399" />
        <KpiCard label="Attention" value={(totals.overdue_count || 0) + (totals.due_today_count || 0)} accentColor="#fb7185" />
      </div>

      <div className="dashboard-content-split">
        <aside className="panel panel--padded form-card">
          <div className="board-toolbar" style={{ marginBottom: 10 }}>
            <div>
              <div className="dashboard-brand__eyebrow">Workspace lists</div>
              <h2 className="section-title" style={{ marginTop: 8 }}>Select board</h2>
            </div>
            <button type="button" className="button-secondary" onClick={openCreateListModal}>
              New list
            </button>
          </div>

          <div className="list-stack">
            {lists.map((todoList) => (
              <button
                key={todoList.id}
                type="button"
                className={`list-card${todoList.id === activeList?.id ? " list-card--active" : ""}`}
                onClick={() => handleSelectList(todoList.id)}
              >
                <span className="list-card__accent" style={{ backgroundColor: todoList.color || "#1976d2" }} />
                <div className="list-card__content">
                  <div className="list-card__title-row">
                    <span className="list-card__title">{todoList.name}</span>
                    <span className="list-card__count">{todoList.task_count}</span>
                  </div>
                  <div className="list-card__meta">
                    {todoList.todo_count} to do · {todoList.in_progress_count} in progress · {todoList.done_count} done
                  </div>
                </div>
              </button>
            ))}
          </div>

          <div className="list-actions-row">
            <button type="button" className="button-ghost" onClick={openEditListModal} disabled={!activeList}>
              Edit list
            </button>
            <button
              type="button"
              className="button-ghost button-ghost--danger"
              onClick={handleArchiveList}
              disabled={!activeList || lists.length <= 1 || saving}
            >
              Archive
            </button>
          </div>

          <div className="form-card__header" style={{ marginTop: 22 }}>
            <div>
              <div className="dashboard-brand__eyebrow">Quick add</div>
              <h2 className="section-title" style={{ marginTop: 8 }}>Create new task</h2>
            </div>
            <button type="button" className="button-ghost" onClick={() => setForm(initialTaskForm)}>
              Clear
            </button>
          </div>

          <p className="dashboard-copy" style={{ marginTop: 10 }}>
            {activeList ? `Adding work into ${activeList.name}.` : "Add work to the board and move it across the workflow."}
          </p>

          {error ? <p className="error-text">{error}</p> : null}

          <form onSubmit={handleCreate} className="todo-form">
            <label className="field">
              <span className="field__label">Title</span>
              <input
                className="input"
                value={form.title}
                onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
                placeholder="Ship new feature live"
              />
            </label>

            <label className="field">
              <span className="field__label">Priority</span>
              <select
                className="select"
                value={form.priority}
                onChange={(event) => setForm((current) => ({ ...current, priority: event.target.value }))}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </label>

            <label className="field">
              <span className="field__label">Due date</span>
              <input
                className="input"
                type="date"
                value={form.due_date}
                onChange={(event) => setForm((current) => ({ ...current, due_date: event.target.value }))}
              />
            </label>

            <label className="field">
              <span className="field__label">Description</span>
              <textarea
                className="textarea"
                value={form.description}
                onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
                placeholder="Add optional context for the task"
              />
            </label>

            <button type="submit" className="button-primary" disabled={saving || !form.title.trim() || !activeList}>
              {saving ? "Saving..." : "Add task to board"}
            </button>
          </form>
        </aside>

        <div className="panel panel--padded">
          <div className="board-toolbar">
            <div>
              <div className="dashboard-brand__eyebrow">Workflow board</div>
              <h2 className="section-title" style={{ marginTop: 8 }}>
                {activeList ? `${activeList.name} tasks` : "Tasks by status"}
              </h2>
              {activeList?.description ? <div className="dashboard-copy" style={{ marginTop: 8 }}>{activeList.description}</div> : null}
            </div>
            <select className="filter-select" value={filter} onChange={(event) => setFilter(event.target.value)}>
              <option value="all">All tasks</option>
              <option value="overdue">Overdue only</option>
              <option value="today">Due today</option>
              <option value="high_priority">High priority</option>
            </select>
          </div>

          {loading ? <p className="dashboard-copy">Loading kanban board...</p> : null}

          <div className="kanban-grid">
            {columns.map((column) => {
              const columnItems = groupedItems[column.key] || [];
              return (
                <section key={column.key} className="kanban-column">
                  <div className="column-header">
                    <div className="column-title-wrap">
                      <span className={`column-dot column-dot--${column.key}`} />
                      <div className="column-title">{column.label}</div>
                    </div>
                    <div className="column-count">{columnItems.length}</div>
                  </div>

                  <div className="column-body">
                    {!loading && columnItems.length === 0 ? (
                      <div className="empty-column">No tasks in this lane.</div>
                    ) : (
                      columnItems.map((item) => (
                        <TaskCard
                          key={item.id}
                          item={item}
                          onStatusChange={handleStatusChange}
                          onDelete={handleDelete}
                          onEdit={handleEditOpen}
                        />
                      ))
                    )}
                  </div>
                </section>
              );
            })}
          </div>
        </div>
      </div>

      {editingItem ? (
        <div className="modal-backdrop" onClick={handleEditClose}>
          <div className="modal-card" onClick={(event) => event.stopPropagation()}>
            <div className="form-card__header">
              <div>
                <div className="dashboard-brand__eyebrow">Task editor</div>
                <h2 className="section-title" style={{ marginTop: 8 }}>Edit task</h2>
              </div>
              <button type="button" className="button-ghost" onClick={handleEditClose}>
                Close
              </button>
            </div>

            <form onSubmit={handleEditSave} className="todo-form" style={{ marginTop: 18 }}>
              <label className="field">
                <span className="field__label">Title</span>
                <input
                  className="input"
                  value={editForm.title}
                  onChange={(event) => setEditForm((current) => ({ ...current, title: event.target.value }))}
                />
              </label>

              <label className="field">
                <span className="field__label">Priority</span>
                <select
                  className="select"
                  value={editForm.priority}
                  onChange={(event) => setEditForm((current) => ({ ...current, priority: event.target.value }))}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </label>

              <label className="field">
                <span className="field__label">Due date</span>
                <input
                  className="input"
                  type="date"
                  value={editForm.due_date}
                  onChange={(event) => setEditForm((current) => ({ ...current, due_date: event.target.value }))}
                />
              </label>

              <label className="field">
                <span className="field__label">Description</span>
                <textarea
                  className="textarea"
                  value={editForm.description}
                  onChange={(event) => setEditForm((current) => ({ ...current, description: event.target.value }))}
                />
              </label>

              <div className="modal-actions">
                <button type="button" className="button-ghost" onClick={handleEditClose}>
                  Cancel
                </button>
                <button type="submit" className="button-primary" disabled={saving || !editForm.title.trim()}>
                  {saving ? "Saving..." : "Save changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {listModalMode ? (
        <div className="modal-backdrop" onClick={closeListModal}>
          <div className="modal-card" onClick={(event) => event.stopPropagation()}>
            <div className="form-card__header">
              <div>
                <div className="dashboard-brand__eyebrow">Workspace manager</div>
                <h2 className="section-title" style={{ marginTop: 8 }}>
                  {listModalMode === "edit" ? "Edit list" : "Create list"}
                </h2>
              </div>
              <button type="button" className="button-ghost" onClick={closeListModal}>
                Close
              </button>
            </div>

            <form onSubmit={handleListSave} className="todo-form" style={{ marginTop: 18 }}>
              <label className="field">
                <span className="field__label">List name</span>
                <input
                  className="input"
                  value={listForm.name}
                  onChange={(event) => setListForm((current) => ({ ...current, name: event.target.value }))}
                  placeholder="Stream Tasks"
                />
              </label>

              <label className="field">
                <span className="field__label">Description</span>
                <textarea
                  className="textarea"
                  value={listForm.description}
                  onChange={(event) => setListForm((current) => ({ ...current, description: event.target.value }))}
                  placeholder="Optional summary for this workspace"
                />
              </label>

              <div className="field">
                <span className="field__label">Color</span>
                <div className="color-palette">
                  {listColors.map((color) => (
                    <button
                      key={color}
                      type="button"
                      className={`color-swatch${listForm.color === color ? " color-swatch--active" : ""}`}
                      style={{ backgroundColor: color }}
                      onClick={() => setListForm((current) => ({ ...current, color }))}
                    />
                  ))}
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="button-ghost" onClick={closeListModal}>
                  Cancel
                </button>
                <button type="submit" className="button-primary" disabled={saving || !listForm.name.trim()}>
                  {saving ? "Saving..." : listModalMode === "edit" ? "Save list" : "Create list"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </section>
  );
}
